import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Form,
  Label,
  Pagination,
  Select,
  Table,
} from 'semantic-ui-react';
import { API, isAdmin, showError, timestamp2string } from '../helpers';
import { ITEMS_PER_PAGE } from '../constants';
import { renderQuota } from '../helpers/render';

function renderTimestamp(timestamp, t) {
  if (!timestamp) {
    return t('payment_orders.table.not_paid');
  }
  return timestamp2string(timestamp);
}

function renderAmount(amount) {
  return `\u00a5${(Number(amount || 0) / 100).toFixed(2)}`;
}

function renderStatus(status, t) {
  switch (status) {
    case 'pending':
      return (
        <Label basic color='yellow'>
          {t('payment_orders.status.pending')}
        </Label>
      );
    case 'paid':
      return (
        <Label basic color='green'>
          {t('payment_orders.status.paid')}
        </Label>
      );
    case 'closed':
      return (
        <Label basic color='grey'>
          {t('payment_orders.status.closed')}
        </Label>
      );
    default:
      return (
        <Label basic color='black'>
          {t('payment_orders.status.unknown')}
        </Label>
      );
  }
}

const PaymentOrdersTable = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState(0);
  // 支付订单默认只看已支付，普通用户用于核对自己的充值记录，管理员用于核对实际入账。
  const [status, setStatus] = useState('paid');
  const [searchKeyword, setSearchKeyword] = useState('');
  const isAdminUser = isAdmin();

  const statusOptions = [
    { key: 'paid', text: t('payment_orders.status.paid'), value: 'paid' },
    {
      key: 'pending',
      text: t('payment_orders.status.pending'),
      value: 'pending',
    },
    { key: 'closed', text: t('payment_orders.status.closed'), value: 'closed' },
    { key: 'all', text: t('payment_orders.status.all'), value: '' },
  ];

  const loadOrders = async (page = 1, localStatus = status) => {
    setLoading(true);
    // 普通用户只能访问自己的订单接口；管理员仍使用全量接口排查跨用户支付问题。
    const params = new URLSearchParams({
      p: String(page - 1),
      page_size: String(ITEMS_PER_PAGE),
    });
    if (localStatus) {
      params.set('status', localStatus);
    }
    if (searchKeyword) {
      params.set('keyword', searchKeyword);
    }

    const endpoint = isAdminUser
      ? '/api/payment/admin/orders'
      : '/api/payment/orders';
    const res = await API.get(`${endpoint}?${params.toString()}`);
    const { success, message, data } = res.data || {};
    if (success) {
      setOrders(data?.items || []);
      setTotal(data?.total || 0);
      setActivePage(page);
    } else {
      showError(message);
    }
    setLoading(false);
  };

  useEffect(() => {
    // 初次进入订单页只加载默认的已支付订单；后续筛选由查询和状态切换主动触发。
    loadOrders(1, 'paid').catch((reason) => {
      showError(reason);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPaginationChange = async (e, { activePage }) => {
    await loadOrders(activePage);
  };

  const handleStatusChange = async (e, { value }) => {
    setStatus(value);
    await loadOrders(1, value);
  };

  const searchOrders = async () => {
    await loadOrders(1);
  };

  const refresh = async () => {
    await loadOrders(activePage);
  };

  const totalPages = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));

  return (
    <>
      <Table basic='very' compact size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t('payment_orders.table.id')}</Table.HeaderCell>
            <Table.HeaderCell>{t('payment_orders.table.order_no')}</Table.HeaderCell>
            <Table.HeaderCell>{t('payment_orders.table.amount')}</Table.HeaderCell>
            <Table.HeaderCell>{t('payment_orders.table.status')}</Table.HeaderCell>
            <Table.HeaderCell>{t('payment_orders.table.created_time')}</Table.HeaderCell>
            <Table.HeaderCell>{t('payment_orders.table.paid_time')}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {orders.map((order) => (
            <Table.Row key={order.id}>
              <Table.Cell>{order.id}</Table.Cell>
              <Table.Cell>{order.order_no}</Table.Cell>
              <Table.Cell>{renderAmount(order.amount)}</Table.Cell>
              <Table.Cell>{renderStatus(order.status, t)}</Table.Cell>
              <Table.Cell>{renderTimestamp(order.created_time, t)}</Table.Cell>
              <Table.Cell>{renderTimestamp(order.paid_time, t)}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>

        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan='8'>
              <Button size='small' onClick={refresh} loading={loading}>
                {t('payment_orders.buttons.refresh')}
              </Button>
              <Pagination
                floated='right'
                activePage={activePage}
                onPageChange={onPaginationChange}
                size='small'
                siblingRange={1}
                totalPages={totalPages}
              />
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
    </>
  );
};

export default PaymentOrdersTable;
